import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";
import { TrashIcon } from "@heroicons/react/outline";

const defaultUnit = "after_invoice_date";

const generateKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const mapBaseRows = (baseValue) => {
  return (baseValue ?? []).map((row, index) => ({
    _key: row?._key ?? row?.id ?? `row-${index + 1}`,
    id: row?.id ?? null,
    percent: typeof row?.percent === "number" ? row.percent : Number(row?.percent ?? 0),
    days: typeof row?.days === "number" ? row.days : Number(row?.days ?? 0),
    unit: row?.unit ?? defaultUnit
  }));
};

const areRowsEqual = (left, right) => {
  if (left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i += 1) {
    const a = left[i];
    const b = right[i];
    if (
      a._key !== b._key ||
      a.id !== b.id ||
      Number(a.percent ?? 0) !== Number(b.percent ?? 0) ||
      Number(a.days ?? 0) !== Number(b.days ?? 0) ||
      a.unit !== b.unit
    ) {
      return false;
    }
  }
  return true;
};

const PaymentTermsConditions = ({ onChange, baseValue = [], translationBase = "company.form" }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState(mapBaseRows(baseValue));

  useEffect(() => {
    const mapped = mapBaseRows(baseValue);
    setRows((prev) => (areRowsEqual(prev, mapped) ? prev : mapped));
  }, [baseValue]);

  useEffect(() => {
    onChange(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const handleChange = (id, field, value) => {
    setRows((prev) => prev.map((row) => (row._key === id ? { ...row, [field]: value } : row)));
  };

  const addLine = (event) => {
    event.preventDefault();
    const newRow = {
      _key: generateKey(),
      id: null,
      percent: 0,
      days: 0,
      unit: defaultUnit
    };
    setRows((prev) => [...prev, newRow]);
  };

  const deleteLine = (id) => {
    setRows((prev) => prev.filter((row) => row._key !== id));
  };

  return (
    <div>
      <h2 className="mb-2 text-[13px] font-normal text-primary-base">
        {t(`${translationBase}.due_terms_title`, { defaultValue: "Installments" })}
      </h2>
      <div className="w-full max-w-3xl rounded border bg-white p-2">
        <table className="w-full text-sm">
          <thead className="border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="py-2">
                {t(`${translationBase}.due_terms_due`, { defaultValue: "Due" })}
              </th>
              <th className="w-48 py-2">
                {t(`${translationBase}.due_terms_after`, { defaultValue: "Schedule" })}
              </th>
              <th className="w-8 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._key} className="border-b last:border-none">
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <NumberFormat
                      value={row.percent ?? 0}
                      className="flex h-8 w-20 rounded-md border border-gray-300 bg-white px-2 text-sm"
                      inputMode="decimal"
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      onValueChange={(values) => {
                        handleChange(row._key, "percent", Number(values.floatValue ?? 0));
                      }}
                      placeholder={t(`${translationBase}.due_terms_percent`, { defaultValue: "%" })}
                      suffix=" %"
                    />
                  </div>
                </td>
                <td className="py-2">
                  <div className="flex items-center">
                    <NumberFormat
                      value={row.days ?? 0}
                      className="flex h-8 w-20 rounded-md border border-gray-300 bg-white px-2 text-sm"
                      inputMode="decimal"
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      onValueChange={(values) => {
                        handleChange(row._key, "days", Number(values.floatValue ?? 0));
                      }}
                      placeholder={t(`${translationBase}.due_terms_value`, { defaultValue: "Days" })}
                    />
                    <select
                      className="ml-2 h-8 w-44 rounded border border-gray-300 bg-white px-2 text-sm leading-tight"
                      value={row.unit}
                      onChange={(event) => handleChange(row._key, "unit", event.target.value)}
                    >
                      <option value="after_invoice_date">
                        {t(`${translationBase}.due_terms_unit_after_invoice_date`, { defaultValue: "after invoice date" })}
                      </option>
                      <option value="after_end_of_month">
                        {t(`${translationBase}.due_terms_unit_after_end_of_month`, { defaultValue: "after end of month" })}
                      </option>
                      <option value="after_end_of_next_month">
                        {t(`${translationBase}.due_terms_unit_after_end_of_next_month`, { defaultValue: "after end of next month" })}
                      </option>
                    </select>
                  </div>
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      deleteLine(row._key);
                    }}
                    className="text-red-500 hover:text-red-700"
                    title={t(`${translationBase}.due_terms_remove_line`, { defaultValue: "Remove condition" })}
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={addLine}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          {t(`${translationBase}.due_terms_add_line`, { defaultValue: "Add condition" })}
        </button>
      </div>
    </div>
  );
};

export default PaymentTermsConditions;
